import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, LIST, ADD, EDIT } from "const"
import { QidCompany } from "server/models"
import { StateType, deepCopy, hasAccess } from "client/utils"
import { qidCompaniesRoute } from "client/parts/routes"
import {
    PRM_QID_COMPANY,
    StoreType,
    QidCompanyList,
    QidCompanyFilters,

    LoadAllQidCompanies,
    ChangeQidCompanyStatus,
    DeleteQidCompany,
    SortQidCompanies,

    setQidCompanyList,
    setQidCompanyIds,
    setQidCompanySort,
    setQidCompanyFilters,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    Button,
    Table,
    TableSearchWrapper,
    SortRows,
    ChangeStatus,
    DeleteRow,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import QidCompanySearch from "client/pages/search/qidCompanySearch"
import Columns from "./columns"

const List = () => {
    const companyList = useSelector((state: StoreType) => state.qidCompany.companyList)
    const companyIds = useSelector((state: StoreType) => state.qidCompany.companyIds)
    const sortColumn = useSelector((state: StoreType) => state.qidCompany.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.qidCompany.sortOrder)
    const filters = useSelector((state: StoreType) => state.qidCompany.filters)
    const reloadList = useSelector((state: StoreType) => state.qidCompany.reloadList)

    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const editAccess = hasAccess(PRM_QID_COMPANY, EDIT)
    const onlyActive = !editAccess

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllQidCompanies(onlyActive).then((res) => {
            dispatch(setQidCompanyList(res))
            setLoadState(IDLE)
        })
    }, [reloadList])

    useEffect(() => {
        const sortedIds = SortQidCompanies(Object.values(companyList), sortColumn, sortOrder, filters)
        dispatch(setQidCompanyIds(sortedIds))
    }, [companyList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof QidCompany, order) => {
        dispatch(setQidCompanySort(column, order))
    }

    const onFilter = (newFilters: QidCompanyFilters) => {
        dispatch(setQidCompanyFilters(newFilters))
    }

    const onStatusChange: ChangeStatus = (company: QidCompany, callback) => {
        ChangeQidCompanyStatus(company).then((status) => {
            callback(() => {
                const companyListCopy: QidCompanyList = deepCopy(companyList)
                companyListCopy[company.id] = {
                    ...companyListCopy[company.id],
                    status,
                }
                dispatch(setQidCompanyList(companyListCopy))
            })
        })
    }

    const onDelete: DeleteRow = (company: QidCompany, callback) => {
        DeleteQidCompany(company.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const companyListCopy: QidCompanyList = companyList ? deepCopy(companyList) : {}
                delete companyListCopy[company.id]
                dispatch(setQidCompanyList(companyListCopy))
                dispatch(setQidCompanyIds(companyIds.filter((id) => id !== company.id)))
            })
        })
    }

    const columns = Columns()

    const listAccess = hasAccess(PRM_QID_COMPANY, LIST)
    const addAccess = hasAccess(PRM_QID_COMPANY, ADD)

    if (!listAccess) return <AccessDenied />
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.companies}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <TableSearchWrapper>
                    <QidCompanySearch
                        id="companyList"
                        rightOffset={addAccess}
                        shortView={onlyActive}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        addAccess && (
                            <Button
                                variant="success"
                                onClick={() => dispatch(addModal({ url: `${qidCompaniesRoute}/${EDIT}/0` }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={companyList}
                    dataIds={companyIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="md"
                    showPagination
                    onSort={onSort}
                    onStatusChange={onStatusChange}
                    onDelete={onDelete}
                />
            </PageContent>
        </Container>
    )
}
export default List
